// =============================================================================
// Resource Schedule Editor - Gestione Orari Settimanali
// =============================================================================

import { useState, useEffect } from 'react';
import { Clock, Copy, Save, Loader2, AlertCircle, CalendarRange } from 'lucide-react';
import {
  useResourceSchedule,
  useTenantDetails,
  useUpdateResourceSchedule,
  WeeklyScheduleItem,
} from '../../api/hooks';
import { businessHoursForDay } from '../../lib/businessHours';
import { useTranslation } from 'react-i18next';
import { Toggle } from '../ui/Toggle';
import { ResourceExceptionsPanel } from './ResourceExceptionsPanel';

type ScheduleTab = 'weekly' | 'exceptions';

interface ResourceScheduleEditorProps {
  resourceId: string;
  resourceName: string;
  onClose: () => void;
}

// L'ordine dell'array e' anche l'ordine di rendering: la settimana parte dal
// lunedi', come negli orari di apertura dell'organizzazione. `dayOfWeek` resta
// l'indice JS (0 = domenica), che e' quello atteso dall'API.
const DEFAULT_SCHEDULE: WeeklyScheduleItem[] = [
  { dayOfWeek: 1, dayName: 'monday', isWorkingDay: true, startTime: '09:00', endTime: '18:00' },
  { dayOfWeek: 2, dayName: 'tuesday', isWorkingDay: true, startTime: '09:00', endTime: '18:00' },
  { dayOfWeek: 3, dayName: 'wednesday', isWorkingDay: true, startTime: '09:00', endTime: '18:00' },
  { dayOfWeek: 4, dayName: 'thursday', isWorkingDay: true, startTime: '09:00', endTime: '18:00' },
  { dayOfWeek: 5, dayName: 'friday', isWorkingDay: true, startTime: '09:00', endTime: '18:00' },
  { dayOfWeek: 6, dayName: 'saturday', isWorkingDay: false },
  { dayOfWeek: 0, dayName: 'sunday', isWorkingDay: false },
];

export function ResourceScheduleEditor({ resourceId, resourceName, onClose }: ResourceScheduleEditorProps) {
  const { data: savedSchedule, isLoading, error } = useResourceSchedule(resourceId);
  const { data: tenant } = useTenantDetails();
  const updateSchedule = useUpdateResourceSchedule();
  const { t } = useTranslation('settings');
  
  const [schedule, setSchedule] = useState<WeeklyScheduleItem[]>(DEFAULT_SCHEDULE);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<ScheduleTab>('weekly');

  // Inizializza con i dati salvati
  useEffect(() => {
    if (savedSchedule && savedSchedule.length > 0) {
      // Merge con DEFAULT_SCHEDULE per preservare dayName (il backend non lo invia)
      const merged = DEFAULT_SCHEDULE.map((defaultDay) => {
        const saved = savedSchedule.find((s) => s.dayOfWeek === defaultDay.dayOfWeek);
        return saved ? { ...saved, dayName: defaultDay.dayName } : defaultDay;
      });
      setSchedule(merged);
      setHasChanges(false);
    }
  }, [savedSchedule]);

  const handleToggleDay = (dayOfWeek: number) => {
    // Abilitando un giorno si parte dagli orari di apertura dell'organizzazione
    // per quel giorno: se e' chiusa (o non li ha configurati) restano i fallback
    // generici, comunque modificabili a mano.
    const openingHours = businessHoursForDay(tenant?.settings?.businessHours, dayOfWeek);

    setSchedule(prev => prev.map(day => {
      if (day.dayOfWeek === dayOfWeek) {
        const newIsWorkingDay = !day.isWorkingDay;
        return {
          ...day,
          isWorkingDay: newIsWorkingDay,
          startTime: newIsWorkingDay ? (day.startTime || openingHours?.start || '09:00') : undefined,
          endTime: newIsWorkingDay ? (day.endTime || openingHours?.end || '18:00') : undefined,
        };
      }
      return day;
    }));
    setHasChanges(true);
  };

  const handleTimeChange = (dayOfWeek: number, field: 'startTime' | 'endTime', value: string) => {
    setSchedule(prev => prev.map(day => {
      if (day.dayOfWeek === dayOfWeek) {
        return { ...day, [field]: value };
      }
      return day;
    }));
    setHasChanges(true);
  };

  const handleCopyToAll = (sourceDayOfWeek: number) => {
    const sourceDay = schedule.find(d => d.dayOfWeek === sourceDayOfWeek);
    if (!sourceDay || !sourceDay.isWorkingDay) return;

    setSchedule(prev => prev.map(day => {
      if (day.dayOfWeek === sourceDayOfWeek) return day;
      return {
        ...day,
        isWorkingDay: true,
        startTime: sourceDay.startTime,
        endTime: sourceDay.endTime,
      };
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      // Rimuovi dayName (solo per UI) - il backend accetta solo dayOfWeek, isWorkingDay, startTime, endTime
      const cleanSchedule = schedule.map(({ dayName, ...rest }) => rest);
      
      await updateSchedule.mutateAsync({
        resourceId,
        schedule: cleanSchedule,
      });
      setHasChanges(false);
      onClose();
    } catch (error) {
      console.error('Error saving schedule:', error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto flex flex-col">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Clock className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('schedule.title')}</h3>
                <p className="text-sm text-gray-500">{resourceName}</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 mt-4 -mb-px">
              <TabButton
                active={activeTab === 'weekly'}
                onClick={() => setActiveTab('weekly')}
                icon={<Clock className="w-4 h-4" />}
                label={t('schedule.tabWeekly')}
              />
              <TabButton
                active={activeTab === 'exceptions'}
                onClick={() => setActiveTab('exceptions')}
                icon={<CalendarRange className="w-4 h-4" />}
                label={t('schedule.tabExceptions')}
              />
            </div>
          </div>

          {/* ===== EXCEPTIONS TAB ===== */}
          {activeTab === 'exceptions' && (
            <>
              <div className="p-6">
                <ResourceExceptionsPanel resourceId={resourceId} />
              </div>
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {t('common:buttons.close')}
                </button>
              </div>
            </>
          )}

          {/* ===== WEEKLY TAB ===== */}
          {activeTab === 'weekly' && isLoading && (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mx-auto" />
              <p className="text-gray-500 mt-4">{t('schedule.loading')}</p>
            </div>
          )}

          {activeTab === 'weekly' && error && (
            <div className="p-12 text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
              <p className="text-gray-600 mt-4">{t('schedule.loadError')}</p>
            </div>
          )}

          {activeTab === 'weekly' && !isLoading && !error && (
            <>
          {/* Content */}
          <div className="p-6 space-y-4">
            {schedule.map((day) => (
              <div
                key={day.dayOfWeek}
                className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${
                  day.isWorkingDay
                    ? 'bg-white border-gray-200'
                    : 'bg-gray-50 border-gray-100'
                }`}
              >
                {/* Day Name & Toggle */}
                <div className="w-32 flex items-center gap-3">
                  <Toggle 
                        checked={day.isWorkingDay}
                        onChange={() => handleToggleDay(day.dayOfWeek)}
                    />
                  <span className={`font-medium ${day.isWorkingDay ? 'text-gray-900' : 'text-gray-400'}`}>
                    {t(`common:days.${day.dayName}`)}
                  </span>
                </div>

                {/* Time Inputs */}
                {day.isWorkingDay ? (
                  <div className="flex items-center gap-3 flex-1">
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-500">{t('schedule.from')}</label>
                      <input
                        type="time"
                        value={day.startTime || '09:00'}
                        onChange={(e) => handleTimeChange(day.dayOfWeek, 'startTime', e.target.value)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-500">{t('schedule.to')}</label>
                      <input
                        type="time"
                        value={day.endTime || '18:00'}
                        onChange={(e) => handleTimeChange(day.dayOfWeek, 'endTime', e.target.value)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                    
                    {/* Copy to all button */}
                    <button
                      onClick={() => handleCopyToAll(day.dayOfWeek)}
                      className="ml-auto p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                      title={t('schedule.copyToAll')}
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex-1 text-sm text-gray-400 italic">
                    {t('schedule.dayOff')}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {hasChanges ? t('schedule.unsaved') : t('schedule.noChanges')}
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('common:buttons.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges || updateSchedule.isPending}
                className="btn-primary flex items-center gap-2 px-4 py-2"
              >
                {updateSchedule.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {t('schedule.saveSchedule')}
              </button>
            </div>
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// TabButton — pulsante tab del modal orari/eccezioni
// -----------------------------------------------------------------------------
function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-emerald-500 text-emerald-700'
          : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
